<template>
  <v-dialog v-model="dialogLocal" max-width="1200px" persistent scrollable>
    <v-card class="document-modal-card">
      <v-toolbar flat dense color="grey lighten-4">
        <v-toolbar-title class="font-weight-medium">
          {{ title }}
        </v-toolbar-title>

        <v-spacer />

        <v-btn icon @click="cargarDocumento" :disabled="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>

        <v-btn icon @click="descargarDocumento" :disabled="!documentUrl || loading">
          <v-icon>mdi-download</v-icon>
        </v-btn>

        <v-btn icon @click="compartirNativo" :disabled="!canNativeShare">
          <v-icon>mdi-cellphone-share</v-icon>
        </v-btn>

        <v-btn icon @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text class="pa-0 document-wrapper">
        <div v-if="loading" class="document-state">
          <v-progress-circular indeterminate size="42" />
          <div class="mt-3">Cargando documento...</div>
        </div>

        <div v-else-if="error" class="document-state error-state">
          <v-icon large color="error">mdi-file-alert-outline</v-icon>
          <div class="mt-3">{{ error }}</div>
        </div>
        <div v-else-if="documentUrl && isPdf" class="pdf-container">
          <embed :src="documentUrl" type="application/pdf" class="document-frame" />

          <div class="pdf-fallback">
            <v-icon color="primary">mdi-file-pdf-box</v-icon>

            <span class="ml-2">
              Si no se visualiza el PDF, descárgalo.
            </span>

            <v-btn small color="primary" class="ml-3" @click="descargarDocumento">
              Descargar
            </v-btn>
          </div>
        </div>

        <!-- XML -->
        <pre v-else-if="xmlContent && isXml" class="xml-viewer">{{ xmlContent }}</pre>

        <!-- IMAGEN -->
        <img v-else-if="documentUrl && isImage" :src="documentUrl" class="image-viewer" />


        <div v-else class="document-state">
          <v-icon large>mdi-file-document-outline</v-icon>
          <div class="mt-3">No hay documento para mostrar.</div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "DocumentModal",

  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: "Vista previa"
    },
    fileName: {
      type: String,
      default: "documento.pdf"
    },
    fileType: {
      required: false,
      type: String,
      default: "pdf" // pdf | xml
    },
    fetchDocument: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      dialogLocal: false,
      loading: false,
      error: null,
      documentUrl: null,
      xmlContent: null,
      documentBlob: null
    };
  },

  computed: {
    isPdf() {
      return this.fileType.toLowerCase() === "pdf";
    },

    isXml() {
      return this.fileType.toLowerCase() === "xml";
    },
    isImage() {
      return this.fileType.toLowerCase() === "image";
    },
    contentType() {
      if (this.isXml) return "application/xml";
      if (this.isImage) return "image/png";
      return "application/pdf";
    },

    canNativeShare() {
      return !!navigator.share;
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.dialogLocal = val;

        if (val) {
          this.cargarDocumento();
        } else {
          this.limpiarDocumento();
        }
      }
    },

    dialogLocal(val) {
      this.$emit("input", val);

      if (!val) {
        this.limpiarDocumento();
      }
    }
  },

  methods: {
    async cargarDocumento() {
      this.loading = true;
      this.error = null;

      try {
        this.limpiarDocumento();

        const response = await this.fetchDocument();

        if (!response || !response.data) {
          throw new Error("No se recibió el documento.");
        }

        this.documentBlob = new Blob([response.data], {
          type: this.contentType
        });

        this.documentUrl = URL.createObjectURL(this.documentBlob);

        if (this.isXml) {
          this.xmlContent = await this.documentBlob.text();
          this.xmlContent = this.formatearXml(this.xmlContent);
        }
      } catch (error) {
        console.error(error);
        this.error = "No fue posible cargar el documento.";
      } finally {
        this.loading = false;
      }
    },

    descargarDocumento() {
      if (!this.documentUrl) return;

      const link = document.createElement("a");
      link.href = this.documentUrl;
      link.download = this.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async compartirNativo() {
      try {
        await navigator.share({
          title: this.title,
          text: "Te comparto este documento",
          url: window.location.href
        });
      } catch (error) {
        console.error(error);
      }
    },

    cerrar() {
      this.dialogLocal = false;
    },

    limpiarDocumento() {
      if (this.documentUrl) {
        URL.revokeObjectURL(this.documentUrl);
      }

      this.documentUrl = null;
      this.xmlContent = null;
      this.documentBlob = null;
    },

    formatearXml(xml) {
      try {
        const reg = /(>)(<)(\/*)/g;
        let formatted = "";
        let pad = 0;

        xml = xml.replace(reg, "$1\r\n$2$3");

        xml.split("\r\n").forEach((node) => {
          let indent = 0;

          if (node.match(/.+<\/\w[^>]*>$/)) {
            indent = 0;
          } else if (node.match(/^<\/\w/)) {
            if (pad !== 0) pad -= 1;
          } else if (node.match(/^<\w([^>]*[^/])?>.*$/)) {
            indent = 1;
          }

          formatted += "  ".repeat(pad) + node + "\r\n";
          pad += indent;
        });

        return formatted.trim();
      } catch {
        return xml;
      }
    }
  },

  beforeDestroy() {
    this.limpiarDocumento();
  }
};
</script>

<style scoped>
.document-modal-card {
  border-radius: 18px;
  overflow: hidden;
}

.document-wrapper {
  height: 80vh;
  background: #f4f6f8;
}

.document-state {
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #616161;
  text-align: center;
  padding: 24px;
}

.error-state {
  color: #c62828;
}

.xml-viewer {
  height: 80vh;
  margin: 0;
  padding: 24px;
  overflow: auto;
  background: #ffffff;
  color: #263238;
  font-size: 13px;
  line-height: 1.6;
  font-family: Consolas, Monaco, "Courier New", monospace;
  white-space: pre-wrap;
}

.image-viewer {
  max-width: 80%;
  max-height: 70vh;
  display: block;
  margin: auto;
  background: white;
  object-fit: contain;
}

.pdf-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 70vh;
}

.document-frame {
  width: 100%;
  height: 100%;
  min-height: 70vh;
  border: none;
}

.pdf-fallback {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
</style>